import React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  IFormOption,
  Select
} from "src/library/components/Forms";

import {
  IUserGroup,
  useUserApi
} from "../api/users.api";

// force parent to hold user state
export interface SelectedUserGroupProps {
  userGroup: IUserGroup | null;
  handleUpdate: (data: IUserGroup | null) => void;
}

const SelectedUserGroup: React.ReactFCWithChildren<SelectedUserGroupProps> = ({ userGroup, handleUpdate }) => {
  const [ userGroupsResponse, getUserGroups ] = useUserApi("LIST_USER_GROUPS");
  // load defaulted to true and is updated when options loaded
  const [ isLoading, setIsLoading ] = React.useState(true);

  // load options if showOptions
  React.useEffect(() => {
    if (!userGroupsResponse.data?.result) {
      // ensure loading state
      setIsLoading(true);

      getUserGroups({
        pagination: {
          page: 0,
          limit: 1000,
          offset: 0,
          sort: [ [ "name", "ASC" ] ]
        },
        filters: {}
      });
    }
  }, [ getUserGroups, userGroupsResponse.data?.result ]);

  // when userGroup updated and options are available, update state
  React.useEffect(() => {
    if (userGroupsResponse.data?.result) {
      setIsLoading(false);
    }
  }, [ userGroup, userGroupsResponse.data?.result ]);

  // generate available options
  const userGroupOptions = React.useMemo(() => {
    let options: IFormOption[] = [];

    if (userGroupsResponse.data) {
      options = _.map(userGroupsResponse.data.result, group => ({
        text: group.name,
        value: group.id.toString()
      }));
    } else {
      return [];
    }

    return options;
  }, [ userGroupsResponse ]);

  return (
    <SelectedUserGroupWrapper>
      {isLoading ?
        (
          <UserGroupLoader>
            <LoadingSpinner
              type="flat"
              position="inline"
            />
          </UserGroupLoader>
        ) : (
          <Select
            as="select"
            name="userGroup"
            label="User Group"
            autoHideLabels
            handleChange={e => {
              setIsLoading(true);

              if (userGroupsResponse.data?.result) {
                return handleUpdate(_.find(userGroupsResponse.data.result, ug => ug.id === parseInt(e.target.value)) || null);
              }
            }}
            options={userGroupOptions}
            unselectedOption={{
              text: " - ",
              value: ""
            }}
            value={userGroup?.id || ""}
          />
        )}

      {userGroup && (
        <div className="remove-user-group">
          <Icon
            className={`${userGroupsResponse.pending ? "disabled" : ""}`}
            icon={Icons.CLOSE}
            $fillColor="primaryDark"
            onClick={() => {
              if (!userGroupsResponse.pending) {
                setIsLoading(true);
                handleUpdate(null);
              }
            }}
          />
        </div>
      )}
    </SelectedUserGroupWrapper>
  );
};

const UserGroupLoader = styled.div`
  max-width: 400px;
  min-width: 160px;
`;

const SelectedUserGroupWrapper = styled.div`
  ${({ theme }) => css`
    max-width: 400px;
    display: flex;

    select:hover {
      border-color: ${theme.colors.midGrey};
      cursor: pointer;
    }

    .remove-user-group {
      margin-left: ${theme.spacing.margins.sm};
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
    
    .disabled {
      cursor: not-allowed;
    }
  `}
`;

export default SelectedUserGroup;
